import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { useAdminFeedbacks } from '@/hooks/use-admin';
import { fullName, timeAgo } from '@/lib/format';

export function AdminFeedbacksPage() {
  const [tab, setTab] = useState<'CLIENT' | 'ASSOCIATE'>('CLIENT');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAdminFeedbacks({
    role: tab,
    page,
    pageSize: 15,
  });

  const feedbacks = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">User Feedbacks</h1>
          <p className="text-sm text-gray-600">
            Review feedbacks shared by clients and associates globally.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200 pb-2">
        {(['CLIENT', 'ASSOCIATE'] as const).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setPage(1);
            }}
            className={cn(
              'shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap',
              tab === t
                ? 'bg-brand-700 text-white shadow-xs'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60',
            )}
          >
            {t === 'CLIENT' ? 'Client Feedbacks' : 'Associate Feedbacks'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="mt-12 text-center text-sm text-gray-500">Loading feedbacks…</div>
      ) : feedbacks.length === 0 ? (
        <div className="mt-12 text-center py-12 rounded-xl border border-dashed border-gray-200 bg-white">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 className="mt-4 text-sm font-semibold text-gray-900">No feedbacks</h3>
          <p className="mt-1 text-sm text-gray-500">No feedbacks have been submitted yet.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {feedbacks.map((fb) => (
              <div
                key={fb.id}
                className="flex flex-col justify-between rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm hover:shadow transition-shadow"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {fullName(fb.user.firstName, fb.user.lastName)}
                      </p>
                      <p className="text-xs text-gray-500">{fb.user.email}</p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-semibold text-gray-600 border border-gray-100 uppercase">
                      {fb.user.role}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-line">
                    {fb.content}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-50 flex items-center justify-between text-[10px] text-gray-400">
                  <span>ID: {fb.id.slice(0, 8)}</span>
                  <span>{timeAgo(fb.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-6">
              <span className="text-xs text-gray-600">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  className="px-3 py-1 text-xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  className="px-3 py-1 text-xs"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
